/**
 * Validation rules for pre-chat / offline form fields.
 *
 * Extracted verbatim from the three copies that lived in PreChatForm,
 * InlineCaptureForm and OfflineForm so every capture surface validates
 * identically. Behavior is unchanged from those copies.
 *
 * Authoritative validation lives in the backend — this is the client-side
 * pass that keeps obviously-wrong input from being submitted.
 *
 * Note: `PreChatFormField.rules` (min_length, pattern, allow_domains, …) is
 * part of the config contract but is not enforced anywhere yet. Keeping that
 * gap as-is here — wiring it up is a behavior change, not a refactor.
 */

import type { PreChatFormField } from '../client/api-types';
import { t } from './i18n';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 15;

/**
 * Validate a single field. Returns the localized error message, or `null`
 * when the value is acceptable.
 *
 * The value is trimmed before every check, so a whitespace-only string counts
 * as empty. Optional fields left blank always pass: the type checks only run
 * once there is something to check.
 */
export function validateField(
  field: PreChatFormField,
  rawValue: string | undefined,
): string | null {
  const val = rawValue?.trim() || '';

  if (field.required && !val) {
    return t('field_required');
  }

  if (field.type === 'email' && val && !EMAIL_PATTERN.test(val)) {
    return t('invalid_email');
  }

  if (field.type === 'phone' && val) {
    const digits = val.replace(/\D/g, '');
    if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
      return t('invalid_phone');
    }
  }

  return null;
}

/**
 * Validate every field against the current values. Returns a map of field
 * name to error message, containing only the fields that failed — an empty
 * object means the form is valid.
 */
export function validateFields(
  fields: PreChatFormField[],
  values: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    const error = validateField(field, values[field.name]);
    if (error) {
      errors[field.name] = error;
    }
  }

  return errors;
}
